import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EntitySelection, Omittable } from "@salestrak/ui";

import { useDebouncedCallback } from "#app/lib/hooks/use-debounced-callback";

/** How long the picker waits after the last toggle before it writes. */
const COMMIT_DELAY_MS = 300;

export type UseDraftedSelectionParams = Omittable<{
  /** The saved selections. Re-seeds the draft while the picker is closed. */
  initialSelections: EntitySelection[];
  /** Writes the draft to the server, debounced; skipped while `draft` is set. */
  flush: (selections: EntitySelection[]) => void;
  /**
   * Create mode: the picker edits a form draft instead of an entity, so every
   * change writes here synchronously and nothing is debounced or prompted.
   */
  draft?: ((selections: EntitySelection[]) => void) | undefined;
  /**
   * Runs when the picker closes with ids that were not selected when it
   * opened: the grant-access prompt fires once per session, after the
   * dropdown closes, instead of mid-selection on every toggle. Never runs in
   * create mode.
   */
  onClosedWithAdditions?: (addedIds: string[]) => void;
}>;

export type DraftedSelectionController = {
  draftSelections: EntitySelection[];
  /** The draft as of now; for handlers that read it outside a render. */
  draftRef: React.RefObject<EntitySelection[]>;
  /** Replace the draft and commit it (debounced, or to the form draft). */
  setDraft: (next: EntitySelection[]) => void;
  handleOpenChange: (nextOpen: boolean) => void;
};

/**
 * The draft-then-commit model every entity picker on an issue shares: the
 * picker edits a local draft, the draft writes after a short debounce, and
 * the saved value only re-seeds the draft while the picker is closed, so a
 * server echo never stomps a selection mid-session.
 */
export function useDraftedSelection({
  initialSelections,
  flush,
  draft,
  onClosedWithAdditions,
}: UseDraftedSelectionParams): DraftedSelectionController {
  const [draftSelections, setDraftSelections] =
    useState<EntitySelection[]>(initialSelections);
  const draftRef = useRef(initialSelections);
  const isOpenRef = useRef(false);
  const idsAtOpenRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isOpenRef.current) {
      draftRef.current = initialSelections;
      setDraftSelections(initialSelections);
    }
  }, [initialSelections]);

  // The pick not yet written, so a close or an unmount writes it at once.
  const pendingRef = useRef<EntitySelection[] | null>(null);
  const flushRef = useRef(flush);
  flushRef.current = flush;
  const write = useCallback((selections: EntitySelection[]) => {
    pendingRef.current = null;
    flushRef.current(selections);
  }, []);
  const { debounced: scheduleFlush, cancel } = useDebouncedCallback(write, COMMIT_DELAY_MS);
  const flushNow = useCallback(() => {
    cancel();
    if (pendingRef.current) write(pendingRef.current);
  }, [cancel, write]);

  // A pick made just before the panel closes still saves.
  useEffect(() => flushNow, [flushNow]);

  const setDraft = useCallback(
    (next: EntitySelection[]) => {
      draftRef.current = next;
      setDraftSelections(next);
      if (draft) draft(next);
      else {
        pendingRef.current = next;
        scheduleFlush(next);
      }
    },
    [draft, scheduleFlush],
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      isOpenRef.current = nextOpen;
      if (nextOpen) {
        draftRef.current = initialSelections;
        setDraftSelections(initialSelections);
        idsAtOpenRef.current = new Set(initialSelections.map((s) => s.id));
        return;
      }
      flushNow();
      if (draft || !onClosedWithAdditions) return;
      const added = draftRef.current
        .map((s) => s.id)
        .filter((id) => !idsAtOpenRef.current.has(id));
      if (added.length) onClosedWithAdditions(added);
    },
    [initialSelections, draft, onClosedWithAdditions, flushNow],
  );

  return useMemo(
    () => ({ draftSelections, draftRef, setDraft, handleOpenChange }),
    [draftSelections, setDraft, handleOpenChange],
  );
}
